import { createContext, useContext, useEffect, useState } from 'react';
import { IPrismicMerchCard, IPrismicSearchPanel, ISearchPanel } from './type';
import { ISearchProduct } from '@monofront/types';
import { useMerchProductsRecommendations } from '@monofront/business/hooks';
import { useShoppingCart } from '@hooks/cart/useShoppingCart';
import { useTagContextMatches } from '@hooks/businessContainer';

interface ISearchPanelProviderProps {
  children?: React.ReactNode;
  prismicSearchPanel: IPrismicSearchPanel[];
}

const SearchPanelContext = createContext<{
  searchPanel?: ISearchPanel;
  hasMerchCard: boolean;
  products: ISearchProduct[] | null | undefined;
  displayCollections: boolean;
  displayBrands: boolean;
}>({
  searchPanel: undefined,
  hasMerchCard: false,
  products: undefined,
  displayCollections: false,
  displayBrands: false,
});

export const SearchPanelProvider: React.FC<ISearchPanelProviderProps> = ({
  children,
  prismicSearchPanel,
}) => {
  const [searchPanel, setSearchPanel] = useState<ISearchPanel | undefined>(
    undefined,
  );
  const { tagContextMatches } = useTagContextMatches();

  const matchingConditionSearchPanel = prismicSearchPanel.find(
    (searchPanel) => {
      return tagContextMatches(searchPanel.condition || '');
    },
  );

  const { shoppingCart } = useShoppingCart();
  const { loading, products } = useMerchProductsRecommendations({
    widgetId: searchPanel?.widgetId ?? '',
    shoppingCart,
  });

  const searchPanelProducts = loading
    ? null
    : products.length > 0
    ? products
    : searchPanel?.products;

  const mapSearchPanel = (
    prismicContent: IPrismicSearchPanel,
  ): ISearchPanel => {
    const { merchImage, merchLink, collections, brands, ...rest } =
      prismicContent;

    const merch: IPrismicMerchCard | null =
      merchImage?.url && merchLink?.link_type
        ? {
            merchImage,
            merchLink,
          }
        : null;

    const formattedCollections = collections?.map((collection) => {
      return { ...collection.collectionLink, ...collection };
    });

    const formattedBrands = brands?.map((brand) => {
      return { ...brand.brandLink, ...brand };
    });

    return {
      ...rest,
      collections: formattedCollections,
      brands: formattedBrands,
      merch,
    };
  };

  const hasMerchCard = !!searchPanel?.merch;
  const panelProducts = hasMerchCard
    ? searchPanelProducts?.slice(0, 3)
    : searchPanelProducts?.slice(0, 6);
  const displayCollections =
    !!searchPanel?.collections?.length && searchPanel.collections?.length > 0;
  const displayBrands =
    !!searchPanel?.brands?.length && searchPanel.brands?.length > 0;

  useEffect(() => {
    if (!matchingConditionSearchPanel) {
      setSearchPanel(undefined);

      return;
    }
    setSearchPanel(mapSearchPanel(matchingConditionSearchPanel));
  }, [matchingConditionSearchPanel]);

  return (
    <SearchPanelContext.Provider
      value={{
        searchPanel,
        hasMerchCard,
        products: panelProducts,
        displayCollections,
        displayBrands,
      }}
    >
      {children}
    </SearchPanelContext.Provider>
  );
};

export const useSearchPanelContext = () => useContext(SearchPanelContext);
